import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Menu, X, ChevronDown, Lock } from 'lucide-react';
import { FlagImg } from '../FlagImg';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const languages = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'nl', label: 'Nederlands' },
];

export const Header = () => {
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/', label: t('home') },
    { path: '/about', label: t('aboutUs') },
    { path: '/products', label: t('products') },
    { path: '/sustainability', label: t('sustainability') },
    { path: '/quality', label: t('quality') },
    { path: '/news', label: t('news') },
    { path: '/contact', label: t('contact') },
  ];

  const privateAreaItems = [
    { key: 'transporters', labelKey: 'privateTransporters' },
    { key: 'purchases', labelKey: 'privatePurchases' },
    { key: 'ecommerce', labelKey: 'privateEcommerce' },
    { key: 'clients', labelKey: 'privateClients' },
  ];

  const currentLanguage = languages.find(l => l.code === language) || languages[1];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handlePrivateAreaClick = (section) => {
    navigate(`/private/login?section=${section}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-slate-900 text-white" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <span className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
              TRH<span className="text-green-500">STEEL</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                className={`px-3 py-2 text-sm font-medium uppercase tracking-wider transition-colors duration-200 border-b-2 ${
                  isActive(item.path)
                    ? 'text-white border-green-500'
                    : 'text-slate-300 border-transparent hover:text-white hover:border-slate-500'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Private Area, Language Selector & Mobile Menu Button */}
          <div className="flex items-center space-x-2">
            {/* Private Area Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="hidden md:flex items-center space-x-2 px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider transition-colors"
                  data-testid="private-area-trigger"
                >
                  <Lock className="h-4 w-4" />
                  <span>{t('privateArea')}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-48">
                {privateAreaItems.map((item) => (
                  <DropdownMenuItem
                    key={item.key}
                    onClick={() => handlePrivateAreaClick(item.key)}
                    className="flex items-center space-x-2 cursor-pointer py-3 text-slate-700 hover:bg-slate-50"
                    data-testid={`private-${item.key}`}
                  >
                    <span className="font-medium">{t(item.labelKey)}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
                  data-testid="language-selector"
                >
                  <span className="text-lg"><FlagImg code={currentLanguage.code} size={20} /></span>
                  <span className="hidden sm:inline">{currentLanguage.label}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex items-center space-x-2 cursor-pointer ${
                      language === lang.code ? 'bg-slate-100' : ''
                    }`}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="text-lg"><FlagImg code={lang.code} size={20} /></span>
                    <span>{lang.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-slate-700" data-testid="mobile-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                className={`block px-4 py-3 text-sm font-medium uppercase tracking-wider transition-colors duration-200 ${
                  isActive(item.path)
                    ? 'text-white bg-slate-800 border-l-4 border-green-500'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800'
                }`}
              >
                {item.label}
              </Link>
            ))}
            {/* Private Area in Mobile */}
            <div className="mt-4 pt-4 border-t border-slate-700">
              <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('privateArea')}
              </div>
              {privateAreaItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => {
                    handlePrivateAreaClick(item.key);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800"
                  data-testid={`mobile-private-${item.key}`}
                >
                  {t(item.labelKey)}
                </button>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
